// Pure matching decision for the modeler's pool → external-party affordance:
// which typed ExternalParty (if any) a BPMN pool name currently resolves to.
// Mirrors the backend map matching (get_external_communication_map): a pool
// resolves to a party when its name equals the party's name or one of its
// aliases after normalization (trim + lowercase); the first party in list
// order claiming the name wins.
import type { ExternalParty } from "@/stores/external-communication-store";

export type PoolPartyResolution =
    /** The pool carries no name at all — nothing to match against. */
    | { kind: "unnamed" }
    /** The pool name already resolves to this typed party. */
    | { kind: "matched"; party: ExternalParty }
    /** Named, but no typed party claims the name — an alias would bind it. */
    | { kind: "needs-alias" };

export const normalizePoolName = (value: string | null | undefined): string =>
    (value ?? "").trim().toLowerCase();

export const poolPartyResolution = (
    poolName: string | null | undefined,
    parties: ExternalParty[]
): PoolPartyResolution => {
    const normalized = normalizePoolName(poolName);
    if (!normalized) {
        return { kind: "unnamed" };
    }
    const party = parties.find(
        (candidate) =>
            normalizePoolName(candidate.name) === normalized ||
            (candidate.aliases ?? []).some(
                (alias) => normalizePoolName(alias) === normalized
            )
    );
    return party ? { kind: "matched", party } : { kind: "needs-alias" };
};
